import React, { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../../context/BrandContext';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, TextField,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TablePagination,
  Stack, Paper, Tooltip, IconButton, Divider
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Close as CloseIcon,
  PrecisionManufacturing as WoIcon
} from '@mui/icons-material';
import bomService from '../../services/bomService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

function statusChip(status) {
  const map = {
    Built:     { color: 'success', label: 'Built' },
    InProcess: { color: 'warning', label: 'In Process' },
    Released:  { color: 'info',    label: 'Released' },
    Planned:   { color: 'default', label: 'Planned' },
    Cancelled: { color: 'error',   label: 'Cancelled' },
  };
  const cfg = map[status] || { color: 'default', label: status || '—' };
  return <Chip size="small" color={cfg.color} label={cfg.label} variant="filled" />;
}

function sourceChip(source) {
  const isCin7 = source === 'cin7';
  return (
    <Chip
      size="small"
      label={source || '—'}
      variant={isCin7 ? 'filled' : 'outlined'}
      color={isCin7 ? 'info' : 'default'}
    />
  );
}

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.startsWith('1970-01-01') || s === '0000-00-00 00:00:00') return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

function fmtDateTime(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.startsWith('1970-01-01') || s === '0000-00-00 00:00:00') return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

export default function WorkOrders() {
  const { selectedBrand } = useBrand();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // drill dialog
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      const data = await bomService.listWorkOrders(selectedBrand.id, params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load work orders'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, page, pageSize, search, statusFilter, sourceFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openDrill = async (woId) => {
    if (!selectedBrand?.id || !woId) return;
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const data = await bomService.drillWorkOrder(selectedBrand.id, woId);
      setDrillData(data?.work_order || data);
    } catch (e) {
      setDrillData({ error: apiErrorMessage(e) });
    } finally {
      setDrillLoading(false);
    }
  };

  if (!selectedBrand?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Select a brand to view its work orders.</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <WoIcon color="primary" />
        <Typography variant="h5">Work Orders</Typography>
        <Typography variant="body2" color="text.secondary">— {selectedBrand.name}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchRows}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Search WO# or assembly SKU"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="Planned">Planned</MenuItem>
                <MenuItem value="Released">Released</MenuItem>
                <MenuItem value="InProcess">In Process</MenuItem>
                <MenuItem value="Built">Built</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All sources</MenuItem>
                <MenuItem value="cin7">cin7</MenuItem>
                <MenuItem value="netsuite">netsuite</MenuItem>
                <MenuItem value="manual">manual</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>WO #</TableCell>
                <TableCell>Assembly SKU</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Qty Planned</TableCell>
                <TableCell>Scheduled End</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Source ID</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No work orders found.</Typography></TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDrill(r.id)}
                >
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.tran_id || `#${r.id}`}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.assembly_sku}</Typography></TableCell>
                  <TableCell>{statusChip(r.status)}</TableCell>
                  <TableCell align="right">{r.quantity_planned}</TableCell>
                  <TableCell>{fmtDate(r.scheduled_end_date)}</TableCell>
                  <TableCell>{sourceChip(r.source)}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{r.source_id || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{r.notes}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Drill dialog */}
      <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center">
            <span>Work Order: {drillData?.tran_id || (drillData?.id ? `#${drillData.id}` : '')}</span>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton onClick={() => setDrillOpen(false)} size="small"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {drillLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : drillData?.error ? (
            <Alert severity="error">{drillData.error}</Alert>
          ) : drillData ? (
            <Box>
              {/* Head info */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    {statusChip(drillData.status)}
                    {sourceChip(drillData.source)}
                    {drillData.order_category ? <Chip size="small" label={drillData.order_category} variant="outlined" /> : null}
                    {drillData.source_id ? <Typography variant="caption" color="text.secondary">source_id: {drillData.source_id}</Typography> : null}
                  </Stack>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Assembly SKU</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{drillData.assembly_sku}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Qty Planned</Typography>
                      <Typography variant="body2">{drillData.quantity_planned}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Scheduled Start</Typography>
                      <Typography variant="body2">{fmtDate(drillData.scheduled_start_date)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Scheduled End</Typography>
                      <Typography variant="body2">{fmtDate(drillData.scheduled_end_date)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">BOM Revision</Typography>
                      <Typography variant="body2">{drillData.bom_revision_id || '—'}</Typography>
                    </Box>
                  </Stack>
                  {drillData.notes ? (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{drillData.notes}</Typography>
                    </>
                  ) : null}
                </CardContent>
              </Card>

              {/* Components */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Components ({(drillData.components || []).length})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Component SKU</TableCell>
                        <TableCell align="right">Qty Required</TableCell>
                        <TableCell align="right">Qty Consumed</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell align="right">Unit Cost</TableCell>
                        <TableCell align="center">Backflushed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(drillData.components || []).length === 0 ? (
                        <TableRow><TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No components</Typography></TableCell></TableRow>
                      ) : drillData.components.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.component_sku}</TableCell>
                          <TableCell align="right">{c.quantity_required}</TableCell>
                          <TableCell align="right">{c.quantity_consumed}</TableCell>
                          <TableCell>{c.unit}</TableCell>
                          <TableCell align="right">
                            {c.unit_cost ? `${c.cost_currency || ''} ${c.unit_cost}` : '—'}
                          </TableCell>
                          <TableCell align="center">{c.is_backflushed ? '✓' : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Outputs */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Outputs ({(drillData.outputs || []).length})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Assembly SKU</TableCell>
                        <TableCell align="right">Qty Planned</TableCell>
                        <TableCell align="right">Qty Delivered</TableCell>
                        <TableCell align="right">Qty Scrapped</TableCell>
                        <TableCell align="right">Unit Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(drillData.outputs || []).length === 0 ? (
                        <TableRow><TableCell colSpan={5}><Typography variant="caption" color="text.secondary">No outputs</Typography></TableCell></TableRow>
                      ) : drillData.outputs.map((o) => (
                        <TableRow key={o.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{o.assembly_sku}</TableCell>
                          <TableCell align="right">{o.quantity_planned}</TableCell>
                          <TableCell align="right">{o.quantity_delivered}</TableCell>
                          <TableCell align="right">{o.quantity_scrapped}</TableCell>
                          <TableCell align="right">
                            {o.unit_cost ? `${o.cost_currency || ''} ${o.unit_cost}` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Status history */}
              {drillData.status_history && drillData.status_history.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Status history ({drillData.status_history.length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>Transitioned at</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drillData.status_history.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell>{statusChip(s.status)}</TableCell>
                            <TableCell>{fmtDateTime(s.transitioned_at)}</TableCell>
                            <TableCell><Typography variant="caption" color="text.secondary">{s.transitioned_by || '—'}</Typography></TableCell>
                            <TableCell><Typography variant="caption" color="text.secondary">{s.notes}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Assembly builds */}
              {drillData.builds && drillData.builds.length > 0 && (
                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Assembly builds ({drillData.builds.length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Build #</TableCell>
                          <TableCell>Build date</TableCell>
                          <TableCell>Assembly SKU</TableCell>
                          <TableCell align="right">Qty Built</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drillData.builds.map((b) => (
                          <TableRow key={b.id}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{b.tran_id || `#${b.id}`}</TableCell>
                            <TableCell>{fmtDate(b.build_date)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{b.assembly_sku}</TableCell>
                            <TableCell align="right">{b.quantity_built}</TableCell>
                            <TableCell align="right">
                              {b.total_cost ? `${b.cost_currency || ''} ${b.total_cost}` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
